/* This file contains some higher level functions that you can use to DRY up
   common redux actions / reducers. If they abstract too much for specific
   use-case, please avoid patching these functions (like adding 100 if conditions),
   but fall-back to the lower-level implementation with your manual changes
   or add separate "default" methods for that purpose */

import { getData, submitData, catchHttpError } from "shared/api";

import {
  defaultInitialState as appDefaultInitialState,
  defaultFetchRequestState as appDefaultFetchRequestState,
  defaultFetchReceiveSuccessState as appDefaultFetchReceiveSuccessState,
  defaultFetchReceiveErrorState as appDefaultFetchReceiveErrorState,
  defaultSubmitRequestState as appDefaultSubmitRequestState,
  defaultSubmitReceiveSuccessState as appDefaultSubmitReceiveSuccessState,
  defaultSubmitReceiveSuccessStateWithoutData as appDefaultSubmitReceiveSuccessStateWithoutData,
  defaultSubmitReceiveErrorState as appDefaultSubmitReceiveErrorState,
} from "shared/redux-store/defaultAppReducerParts";

import {
  defaultInitialState as dataDefaultInitialState,
  defaultReceiveSuccessState as dataDefaultReceiveSuccessState,
} from "shared/redux-store/defaultDataReducerParts";

// Creates a FETCH action that triggers a GET request to all paths given
export function createActionFetcher(actionType, pathsToFetch = []) {
  return function actionFetcher() {
    return dispatch =>
      dispatch({
        type: `FETCH_${actionType}`,
        payload: Promise.all(pathsToFetch.map(getData)),
      }).catch(catchHttpError);
  };
}

export function createAppReducerSimpleFetch(
  actionType,
  resourcesToRetrieve = [],
) {
  return function appReducerSimpleFetch(
    state = appDefaultInitialState(),
    action,
  ) {
    switch (action.type) {
      case `FETCH_${actionType}_PENDING`:
        return {
          ...state,
          ...appDefaultFetchRequestState(state, action),
        };
      case `FETCH_${actionType}_FULFILLED`:
        return {
          ...state,
          ...appDefaultFetchReceiveSuccessState(
            state,
            action,
            resourcesToRetrieve,
          ),
        };
      case `FETCH_${actionType}_REJECTED`:
        return {
          ...state,
          ...appDefaultFetchReceiveErrorState(state, action),
        };
      default:
        return state;
    }
  };
}

export function createDataReducerSimpleFetch(
  actionType,
  resourcesToRetrieve = [],
) {
  return function dataReducerSimpleFetch(
    state = dataDefaultInitialState(),
    action,
  ) {
    switch (action.type) {
      case `FETCH_${actionType}_FULFILLED`:
        return {
          ...state,
          ...dataDefaultReceiveSuccessState(state, action, resourcesToRetrieve),
        };
      default:
        return state;
    }
  };
}

// Creates a SUBMIT action to deliver a payload
//
// The actionSubmitter factory takes not only the action name, but also a collection (array)
// of endpoint definitions. A single endpoint definition look like this:
//
//   {
//     pathFn: …,
//     verb: …,
//   }
//
// The pathFn describes the URL the request should go to. It should be a function, so it can
// be parameterized for things like record ids (similar to rails routes helpers)
// The verb is the HTTP verb (default is PUT)
//
// The actionSubmitter function takes the requestInfo object *at runtime*:
//
//   {
//     pathParams: …,
//     requestData: …,
//   }
//
// The path params will be put into the pathFn and the requestData is the actual payload
//
// TODO when needed: Instead of a single requestInfo object to pass in, this should really
//                   be an array that maps to the respective endpoint definition
//                   (right now the same request info will just be send to all endpoint defs)
export function createActionSubmitter(
  actionType,
  endpointDefinitions = [],
  successCallback = () => {},
) {
  return function actionSubmitter(requestInfo) {
    const pathParams = (requestInfo && requestInfo.pathParams) || [];

    return dispatch =>
      dispatch({
        type: `SUBMIT_${actionType}`,
        payload: Promise.all(
          endpointDefinitions.map(endpointDefinition => {
            return submitData(
              endpointDefinition.pathFn(...pathParams),
              requestInfo && requestInfo.requestData,
              endpointDefinition.verb || "PUT",
            );
          }),
        ).then(response => {
          successCallback(response, dispatch);
          return response;
        }),
      }).catch(catchHttpError);
  };
}

export function createAppReducerSimpleSubmit(
  actionType,
  resourcesToRetrieve = [],
  successMessage = "Erfolgreich",
) {
  return function appReducerSimpleSubmit(
    state = appDefaultInitialState(),
    action,
  ) {
    switch (action.type) {
      case `SUBMIT_${actionType}_PENDING`:
        return {
          ...state,
          ...appDefaultSubmitRequestState(state, action),
        };
      case `SUBMIT_${actionType}_FULFILLED`:
        return {
          ...state,
          ...appDefaultSubmitReceiveSuccessState(
            state,
            action,
            resourcesToRetrieve,
            successMessage,
          ),
        };
      case `SUBMIT_${actionType}_REJECTED`:
        return {
          ...state,
          // TODO last argument should map to proper resource for multiple resources
          // to ensure good error messages
          ...appDefaultSubmitReceiveErrorState(
            state,
            action,
            resourcesToRetrieve[0],
          ),
        };
      default:
        return state;
    }
  };
}

export function createAppReducerSimpleSubmitWithoutData(actionType) {
  return function appReducerSimpleSubmitWithoutData(
    state = appDefaultInitialState(),
    action,
  ) {
    switch (action.type) {
      case `SUBMIT_${actionType}_PENDING`:
        return {
          ...state,
          ...appDefaultSubmitRequestState(state, action),
        };
      case `SUBMIT_${actionType}_FULFILLED`:
        return {
          ...state,
          ...appDefaultSubmitReceiveSuccessStateWithoutData(state, action),
        };
      case `SUBMIT_${actionType}_REJECTED`:
        return {
          ...state,
          ...appDefaultSubmitReceiveErrorState(state, action),
        };
      default:
        return state;
    }
  };
}

export function createDataReducerSimpleSubmit(
  actionType,
  resourcesToRetrieve = [],
) {
  return function dataReducerSimpleSubmit(
    state = dataDefaultInitialState(),
    action,
  ) {
    switch (action.type) {
      case `SUBMIT_${actionType}_FULFILLED`:
        return {
          ...state,
          ...dataDefaultReceiveSuccessState(state, action, resourcesToRetrieve),
        };
      default:
        return state;
    }
  };
}
